import { Badge } from "@/components/ui";
import type { ReferenceGate, ReferenceScores } from "@/types";
import { GATE_TITLE, GATE_TONE, gateLabel, reasonText, score2 } from "./referenceScores";

function Gate({ title, gate, rows }: { title: string; gate: ReferenceGate; rows: Array<[string, string, string]> }) {
  return (
    <div className="grid gap-1 rounded-md border border-border px-2.5 py-2" aria-label={title}>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[11.5px] font-semibold">{title}</span>
        <Badge size="compact" className={GATE_TONE[gate.state]}>
          {gateLabel(gate)}
        </Badge>
      </div>
      <table className="text-[11.5px] tabular-nums">
        <tbody>
          {rows.map(([label, ref, tc]) => (
            <tr key={label}>
              <td className="pr-3 text-muted-foreground">{label}</td>
              <td className="pr-3">Reference {ref}</td>
              <td className="text-muted-foreground">TC 답변 {tc}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {gate.reasons.length > 0 && (
        <ul className="grid gap-0.5 text-[11.5px]">
          {gate.reasons.map((r) => (
            <li key={r} className={gate.state === "flag" ? "text-destructive" : "text-muted-foreground"}>
              {reasonText(r)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * A version's rubric_v3 and common rubric scores beside its TC answer's, read as two gates. Scores are judged
 * right after the version is generated; this block only shows them. 탈락 예정 only marks a version; the
 * reviewer still passes or rejects.
 */
export function ReferenceScoreBlock({ scores }: { scores: ReferenceScores | null | undefined }) {
  const ref = scores?.reference;
  const tc = scores?.tc;
  return (
    <div className="grid gap-1.5" aria-label="Reference 점수">
      <span className="text-[11px] font-semibold text-muted-foreground">
        점수
        {scores?.gates.flagged && (
          <Badge size="compact" className={`ml-1.5 ${GATE_TONE.flag}`}>
            탈락 예정
          </Badge>
        )}
      </span>
      {scores ? (
        <div className="grid gap-1.5 md:grid-cols-2">
          <Gate
            title={GATE_TITLE.needs}
            gate={scores.gates.needs}
            rows={[["점수", score2(ref?.v3?.final), score2(tc?.v3)]]}
          />
          <Gate
            title={GATE_TITLE.quality}
            gate={scores.gates.quality}
            rows={[
              ["답변 품질", score2(ref?.common?.answer_quality), score2(tc?.common?.answer_quality)],
              ["최종", score2(ref?.common?.final), score2(tc?.common?.final)],
            ]}
          />
        </div>
      ) : (
        <p className="text-[11.5px] text-muted-foreground">목록에서 연 버전의 점수만 보여줍니다.</p>
      )}
      <p className="text-[11px] text-muted-foreground">
        탈락 예정은 표시만 합니다. 통과·반려는 직접 정합니다. 품질은 common rubric의 답변 품질 점수끼리 비교합니다.
      </p>
    </div>
  );
}
